import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { devicesApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { CodePill } from "@/components/Atoms";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "@/lib/rbac";

const PROTOCOLS = ["BLE", "LoRaWAN", "WiFi", "Zigbee", "Other"];

function DeviceTypeForm({ initial, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial || { name: "", protocol: "BLE", vendor: "" });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Name is required");
        onSubmit(form);
      }}
      className="space-y-3"
      data-testid="device-type-form"
    >
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Name</span>
        <input
          data-testid="dt-input-name"
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Protocol</span>
        <select
          data-testid="dt-input-protocol"
          value={form.protocol}
          onChange={(e) => setForm({ ...form, protocol: e.target.value })}
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          {PROTOCOLS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Vendor</span>
        <input
          data-testid="dt-input-vendor"
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          value={form.vendor || ""}
          onChange={(e) => setForm({ ...form, vendor: e.target.value })}
        />
      </label>

      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-600">
        <strong>Parsing rules?</strong> Device types are pure metadata.
        To map gateway-emitted frames to a parser, go to{" "}
        <a href="/envelope-rules" className="underline text-zinc-900">Envelope rules → Routes</a>{" "}
        (you can have several routes per canonical_hw).
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">
          Cancel
        </button>
        <button data-testid="dt-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">
          Save
        </button>
      </div>
    </form>
  );
}

export default function DeviceTypes() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const canWrite = useCan("write:device_types");

  const list = useQuery({ queryKey: ["device-types"], queryFn: devicesApi.types });

  const filtered = (list.data || []).filter(
    (t) =>
      !search ||
      (t.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.vendor || "").toLowerCase().includes(search.toLowerCase()) ||
      (t.protocol || "").toLowerCase().includes(search.toLowerCase())
  );
  const pager = usePager(filtered, { defaultSize: 50, sizes: [25, 50, 100] });

  const inval = () => qc.invalidateQueries({ queryKey: ["device-types"] });
  const cM = useMutation({ mutationFn: devicesApi.typeCreate, onSuccess: () => { toast.success("Device type created"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => devicesApi.typeUpdate(id, data), onSuccess: () => { toast.success("Device type updated"); inval(); setModal(null); } });
  const dM = useMutation({ mutationFn: devicesApi.typeRemove, onSuccess: () => { toast.success("Device type deleted"); inval(); } });

  return (
    <PageContainer>
      <PageHeader
        title="Device types"
        subtitle="Catalog of registered device models. Each device instance points to one of these."
        actions={
          canWrite ? (
            <button
              data-testid="dt-new"
              onClick={() => setModal({ mode: "create" })}
              className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> New device type
            </button>
          ) : null
        }
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
            <input
              data-testid="dt-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, vendor or protocol…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
            />
          </div>
          <div className="text-xs text-zinc-500 font-mono">{filtered.length} total</div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Protocol</th>
              <th className="text-left p-3 font-semibold">Vendor</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((t) => (
              <tr key={t.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`dt-row-${t.id}`}>
                <td className="p-3 font-medium">{t.name}</td>
                <td className="p-3"><CodePill>{t.protocol}</CodePill></td>
                <td className="p-3 text-zinc-600">{t.vendor || "—"}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {canWrite && (
                      <>
                        <button
                          data-testid={`dt-edit-${t.id}`}
                          onClick={() => setModal({ mode: "edit", row: t })}
                          className="text-zinc-600 hover:text-zinc-900 p-1.5 rounded hover:bg-zinc-100"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`dt-delete-${t.id}`}
                          onClick={() => { if (window.confirm(`Delete "${t.name}"?`)) dM.mutate(t.id); }}
                          className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {pager.total === 0 && (
              <tr>
                <td colSpan={4} className="py-10 text-center text-sm text-zinc-500">
                  No device types found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="dt-pager" />
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">
              {modal.mode === "create" ? "New device type" : "Edit device type"}
            </h2>
            <DeviceTypeForm
              initial={modal.row}
              onCancel={() => setModal(null)}
              onSubmit={(form) => {
                if (modal.mode === "create") cM.mutate(form);
                else uM.mutate({ id: modal.row.id, data: form });
              }}
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
}
